import {
  type CurrencyExchangeRate,
  invoiceToExchangedValue,
  invoiceValuesOfRows,
  type SelectedCurrencyExchange,
} from "$routes/_lib/billing-v2/currency-exchange";
import type { BillingCurrency } from "elephant-web/gql";

export type PaymentRow = {
  amountInPence?: number;
  currency?: string;
  type?: string;
};

export type ExchangeFor = (
  currency: string | undefined,
) => SelectedCurrencyExchange | undefined;

export const NO_EXCHANGE: ExchangeFor = () => undefined;

export const exchangeForRate =
  (
    latestRate: {
      otherCurrency: BillingCurrency;
      exchangeRate: CurrencyExchangeRate;
    } | null,
  ): ExchangeFor =>
  (currency) =>
    latestRate && currency === latestRate.otherCurrency
      ? { currency: latestRate.otherCurrency, rate: latestRate.exchangeRate }
      : undefined;

export const rowExchange = (row: PaymentRow, exchangeFor: ExchangeFor) =>
  row.type === "WAIVER" ? undefined : exchangeFor(row.currency);

export const hasExchangedRow = (
  rows: readonly PaymentRow[],
  exchangeFor: ExchangeFor,
) => rows.some((row) => rowExchange(row, exchangeFor));

export const invoiceValuesOf = (
  rows: readonly PaymentRow[],
  dueInPence: number,
  exchangeFor: ExchangeFor,
): number[] =>
  hasExchangedRow(rows, exchangeFor)
    ? invoiceValuesOfRows({
        rows: rows.map((row) => ({
          value: row.amountInPence || 0,
          exchange: rowExchange(row, exchangeFor),
        })),
        due: dueInPence,
      })
    : rows.map((row) => row.amountInPence as number);

export const totalInvoiceValueOf = (
  rows: readonly PaymentRow[],
  dueInPence: number,
  exchangeFor: ExchangeFor,
) =>
  invoiceValuesOf(rows, dueInPence, exchangeFor).reduce(
    (total, value) => total + value,
    0,
  );

export const amountLeftForRow = (
  rows: readonly PaymentRow[],
  index: number,
  currency: string,
  dueInPence: number,
  exchangeFor: ExchangeFor,
) => {
  const others = rows.filter((_, i) => i !== index);
  const left = Math.max(
    0,
    dueInPence - totalInvoiceValueOf(others, dueInPence, exchangeFor),
  );
  const exchange = exchangeFor(currency);
  return exchange
    ? invoiceToExchangedValue(left, exchange.currency, exchange.rate)
    : left;
};

export const enteredByCurrency = (
  rows: readonly PaymentRow[],
  invoiceCurrency: string,
): [string, number][] => {
  const totals = new Map<string, number>([[invoiceCurrency, 0]]);
  for (const row of rows) {
    if (row.type === "WAIVER") continue;
    const currency = row.currency ?? invoiceCurrency;
    totals.set(
      currency,
      (totals.get(currency) ?? 0) + (row.amountInPence || 0),
    );
  }
  return [...totals].filter(([, value]) => value > 0);
};
